import { Fragment, useState, useEffect, useMemo, useCallback, useRef } from 'react'
import styled, { createGlobalStyle } from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { GoogleMap, Libraries, useJsApiLoader } from '@react-google-maps/api'
import { renderToStaticMarkup } from 'react-dom/server'
import { CursorGreen, CursorTeebox } from 'assets/images'
import { GreenMarkerIcon } from 'assets/icons/green-marker'
import { TeeboxMarkerIcon } from 'assets/icons/teebox-marker'
import {
  MapAdvancedMarker,
  MapPolygonOverlay,
  MapPolylineOverlay,
} from 'components/gps-editor/map/GoogleOverlayPrimitives'
import LoadingSpinnerGreen from 'components/ui/LoadingSpinnerGreen'
import { selectCourse } from 'store/course/slice'
import { GpsEditorHistorySnapshot, useGpsEditorHistory } from 'components/gps-editor/hooks/useGpsEditorHistory'
import { selectAuth } from 'store/authentication/slice'
import CourseGpsEditorElements from 'components/gps-editor/CourseGpsEditorElements'
import { createCourseGpsVersion, setCourseGpsVersionOfficial } from 'store/course/thunks'
import { COURSE_DATA_SOURCE } from 'config'
import { compareData } from 'utils/compareData'
import { latLonToGlobalPx, globalPxToLatLon } from 'utils/gpsEditor/coordinateConversion'
import { DEFAULT_GREEN_AUTO_MAP_PARAMS, GreenAutoMapParams, runGreenAutoMapForHole } from 'utils/gpsEditor/gpsUtils'
import useUserRole from 'hooks/useUserRole'
import {
  buildCompleteCourseGpsData,
  buildGreenAutoMapInitialParams,
  clonePolygonPoints,
  createDistanceLabelMarker,
  DEFAULT_COURSE_ZOOM,
  DEFAULT_HOLE_ZOOM,
  DEFAULT_MAP_ID,
  DEFAULT_ZOOM_WITHOUT_COURSE,
  DistanceLabelMarker,
  GREEN_AUTO_MAP_DEBUG_PREFERENCES_STORAGE_KEY,
  GREEN_AUTO_MAP_PERSISTED_DEBUG_KEYS,
  GreenAutoMapPersistedDebugKey,
  getHoleLayoutKey,
  getMainMarkerDimensions,
  getOffsetPreviewLineStart,
  getScaledGreenMarkerSizePx,
  LINE_STROKE_WEIGHT,
  MAX_GPS_POINTS,
  MAX_MAP_ZOOM,
  setDistanceLabel,
  setPathDistanceLabel,
  toLatLngPath,
} from './courseGpsEditor.utils'

enum LINE_COLOR {
  Active = '#FFF',
  Front9 = '#FFF',
  Back9 = '#FFF',
}

enum LOCATION_TYPE {
  Green = 'green',
  Teebox = 'teebox',
  Fairway = 'fairway',
}

interface HoleGpsModel {
  hole: number
  teebox?: CourseGpsMarker
  fairway?: CourseGpsMarker
  green?: CourseGpsMarker
  color: string
}

interface StackedHoleLayout {
  key: string
  holes: number[]
  representativeHole: number
  teebox: CourseGpsMarker
  fairway?: CourseGpsMarker
  green: CourseGpsMarker
  rawPath: CourseGpsMarker[]
  path: google.maps.LatLngLiteral[]
}

interface StackedPolygonOverlay {
  key: string
  holes: number[]
  representativeHole: number
  polygon: GpsEditorPolygon
}

interface DisplayMarker extends CourseGpsMarker {
  stackKey: string
  holes: number[]
  representativeHole: number
}

interface GreenAutoMapPreview {
  hole: number
  center: google.maps.LatLngLiteral
  edgePoints: google.maps.LatLngLiteral[]
}

interface GuidedGreenAutoMapSession {
  holes: number[]
}

const LINE_ENDPOINT_OFFSET_RATIO = 0.5
const GREEN_POLYGON_Z_INDEX = -10
const GREEN_AUTO_MAP_PREVIEW_Z_INDEX = 10

const GOOGLE_MAPS_LIBRARIES: Libraries = ['places', 'geometry', 'marker']

const CourseGpsEditor = ({ apiKey }): JSX.Element => {
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [activeHole, setActiveHole] = useState<number>(1)
  const [holeToolEnabled, setHoleToolEnabled] = useState<boolean>(false)
  const [greenMappingEnabled, setGreenMappingEnabled] = useState<boolean>(false)
  const [selectedGpsVersion, setSelectedGpsVersion] = useState<number | null>(null)
  const [zoom, setZoom] = useState<number>(DEFAULT_ZOOM_WITHOUT_COURSE)
  const [mapTiltEnabled, setMapTiltEnabled] = useState<boolean>(false)
  const [mapTiltAvailable, setMapTiltAvailable] = useState<boolean>(false)
  const [mapSearch, setMapSearch] = useState<any>(null)
  const [markers, setMarkers] = useState<CourseGpsMarker[]>([])
  const [poiPolygons, setPoiPolygons] = useState<GpsEditorPolygon[]>([])
  const [greenAutoMapControlsVisible, setGreenAutoMapControlsVisible] = useState<boolean>(false)
  const [greenAutoMapInFlight, setGreenAutoMapInFlight] = useState<boolean>(false)
  const [greenAutoMapParams, setGreenAutoMapParams] = useState<GreenAutoMapParams>(buildGreenAutoMapInitialParams)
  const [greenAutoMapPreview, setGreenAutoMapPreview] = useState<GreenAutoMapPreview | null>(null)
  const [guidedGreenAutoMapSession, setGuidedGreenAutoMapSession] = useState<GuidedGreenAutoMapSession | null>(null)
  const [guidedGreenAutoMapCompletionVisible, setGuidedGreenAutoMapCompletionVisible] = useState<boolean>(false)
  const [draggingFairwayHole, setDraggingFairwayHole] = useState<number | null>(null)
  const [draggingMainMarkerHole, setDraggingMainMarkerHole] = useState<number | null>(null)
  const activeDrawingPolygonIdRef = useRef<string | null>(null)
  const holeNumberMarkerRefs = useRef<Map<number, google.maps.marker.AdvancedMarkerElement>>(new Map())
  const pointMarkerDragSessionRef = useRef<{
    representativeHole: number
    holes: number[]
    locationType: LOCATION_TYPE.Teebox | LOCATION_TYPE.Green
    staticPoint: google.maps.LatLngLiteral
    fairwayPoint: google.maps.LatLngLiteral | null
    polyline: google.maps.Polyline | null
    distanceLabelMarkers: DistanceLabelMarker[]
    didDrag: boolean
  } | null>(null)
  const fairwayDragSessionRef = useRef<{
    representativeHole: number
    holes: number[]
    teebox: google.maps.LatLngLiteral
    green: google.maps.LatLngLiteral
    strokeColor: string
    zIndex: number
    didDrag: boolean
    polyline: google.maps.Polyline | null
    distanceLabelMarkers: DistanceLabelMarker[]
  } | null>(null)
  const pendingGreenAutoAdvanceRef = useRef<{
    hole: number
    shouldAutoMap: boolean
    holeInput: { green: { lat: number; lng: number }; teebox: { lat: number; lng: number } } | null
  } | null>(null)
  const isProcessingPOI = useRef<boolean>(false)
  const lastSyncedVersionRef = useRef<number | null | undefined>(undefined)
  const poiRequestIdRef = useRef(0)
  const isMountedRef = useRef(true)
  const greenAutoMapParamsRef = useRef<GreenAutoMapParams>(buildGreenAutoMapInitialParams())
  const maxZoomServiceRef = useRef<google.maps.MaxZoomService | null>(null)
  const getMaxImageryZoom = useCallback(async (latLng: { lat: number; lng: number }): Promise<number | null> => {
    if (!window.google?.maps?.MaxZoomService) {
      return null
    }
    if (!maxZoomServiceRef.current) {
      maxZoomServiceRef.current = new window.google.maps.MaxZoomService()
    }
    try {
      const result = await maxZoomServiceRef.current.getMaxZoomAtLatLng(latLng)
      if (typeof result.zoom === 'number') {
        return result.zoom
      }
    } catch (error) {
      console.warn('MaxZoomService.getMaxZoomAtLatLng failed', error)
    }
    return null
  }, [])
  const runGreenToolAutoMapRef = useRef<
    | ((
        hole: number,
        holeInput: { green: { lat: number; lng: number }; teebox: { lat: number; lng: number } },
        params: GreenAutoMapParams,
      ) => void)
    | null
  >(null)
  const activateGreenToolRef = useRef<(() => void) | null>(null)
  const registerActivateGreenTool = useCallback((fn: (() => void) | null) => {
    activateGreenToolRef.current = fn
  }, [])
  const [center, setCenter] = useState<any>({
    lat: 60.07108,
    lng: 24.27973,
  })
  const { isAdmin, isAdminCourseEditor, isGpsEditor } = useUserRole()
  const dispatch = useDispatch()
  const { course, gpsVersionStatus } = useSelector(selectCourse)
  const auth = useSelector(selectAuth)

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: apiKey,
    libraries: GOOGLE_MAPS_LIBRARIES,
  })

  const markersRef = useRef<CourseGpsMarker[]>(markers)
  const poiPolygonsRef = useRef<GpsEditorPolygon[]>(poiPolygons)
  const greenAutoMapPreviewRef = useRef<GreenAutoMapPreview | null>(greenAutoMapPreview)
  const greenAutoMapControlsVisibleRef = useRef<boolean>(greenAutoMapControlsVisible)
  useEffect(() => {
    markersRef.current = markers
  }, [markers])
  useEffect(() => {
    poiPolygonsRef.current = poiPolygons
  }, [poiPolygons])
  useEffect(() => {
    greenAutoMapPreviewRef.current = greenAutoMapPreview
  }, [greenAutoMapPreview])
  useEffect(() => {
    greenAutoMapControlsVisibleRef.current = greenAutoMapControlsVisible
  }, [greenAutoMapControlsVisible])

  const getHistorySnapshot = useCallback(
    (): GpsEditorHistorySnapshot => ({
      markers: markersRef.current,
      polygons: poiPolygonsRef.current,
      greenAutoMapPreview: greenAutoMapPreviewRef.current,
      greenAutoMapControlsVisible: greenAutoMapControlsVisibleRef.current,
    }),
    [],
  )

  const restoreHistorySnapshot = useCallback((snapshot: GpsEditorHistorySnapshot) => {
    setMarkers(snapshot.markers)
    setPoiPolygons(snapshot.polygons)
    setGreenAutoMapPreview(snapshot.greenAutoMapPreview)
    setGreenAutoMapControlsVisible(snapshot.greenAutoMapControlsVisible)
    activeDrawingPolygonIdRef.current = null
  }, [])

  const history = useGpsEditorHistory(getHistorySnapshot, restoreHistorySnapshot)

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const activeElement = document.activeElement
      const targetTag = activeElement?.tagName
      const isEditableTarget =
        targetTag === 'INPUT' ||
        targetTag === 'TEXTAREA' ||
        (activeElement instanceof HTMLElement && activeElement.isContentEditable)
      if (isEditableTarget) {
        return
      }

      const isModifier = event.metaKey || event.ctrlKey
      if (!isModifier || event.key.toLowerCase() !== 'z') {
        return
      }

      event.preventDefault()
      if (event.shiftKey) {
        history.redo()
      } else {
        history.undo()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [history])

  const enforceFlatMapTilt = useCallback((targetMap?: google.maps.Map | null) => {
    if (!targetMap) {
      return
    }

    const applyFlatTilt = () => {
      if (typeof targetMap.getTilt === 'function' && targetMap.getTilt() !== 0) {
        targetMap.setTilt(0)
      }
    }

    applyFlatTilt()

    if (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(applyFlatTilt)
    }
  }, [])

  const handleZoomChanged = () => {
    enforceFlatMapTilt(map)

    const mapZoom = map?.getZoom()
    if (mapZoom !== undefined) {
      setZoom(mapZoom)
    }
  }

  const onLoad = useCallback(
    function callback(map) {
      setMap(map)
      enforceFlatMapTilt(map)
    },
    [enforceFlatMapTilt],
  )

  useEffect(() => {
    isMountedRef.current = true

    return () => {
      pointMarkerDragSessionRef.current?.distanceLabelMarkers?.forEach((labelMarker) => {
        labelMarker.marker.map = null
      })
      pointMarkerDragSessionRef.current?.polyline?.setMap(null)
      pointMarkerDragSessionRef.current = null
      fairwayDragSessionRef.current?.distanceLabelMarkers?.forEach((labelMarker) => {
        labelMarker.marker.map = null
      })
      fairwayDragSessionRef.current?.polyline?.setMap(null)
      fairwayDragSessionRef.current = null
      isMountedRef.current = false
      poiRequestIdRef.current += 1
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const persistedPreferences = GREEN_AUTO_MAP_PERSISTED_DEBUG_KEYS.reduce<
      Pick<GreenAutoMapParams, GreenAutoMapPersistedDebugKey>
    >(
      (preferences, key) => ({
        ...preferences,
        [key]: greenAutoMapParams[key],
      }),
      {
        debugStopLogging: DEFAULT_GREEN_AUTO_MAP_PARAMS.debugStopLogging,
        debugStepLogging: DEFAULT_GREEN_AUTO_MAP_PARAMS.debugStepLogging,
        debugImageLogging: DEFAULT_GREEN_AUTO_MAP_PARAMS.debugImageLogging,
      },
    )

    window.localStorage.setItem(GREEN_AUTO_MAP_DEBUG_PREFERENCES_STORAGE_KEY, JSON.stringify(persistedPreferences))
  }, [greenAutoMapParams.debugImageLogging, greenAutoMapParams.debugStepLogging, greenAutoMapParams.debugStopLogging])

  useEffect(() => {
    greenAutoMapParamsRef.current = greenAutoMapParams
  }, [greenAutoMapParams])

  useEffect(() => {
    setGreenAutoMapParams((prev) =>
      prev.rayCount === DEFAULT_GREEN_AUTO_MAP_PARAMS.rayCount
        ? prev
        : { ...prev, rayCount: DEFAULT_GREEN_AUTO_MAP_PARAMS.rayCount },
    )
  }, [activeHole])

  const nonFairwayMarkers = useMemo(() => {
    return markers.filter((marker: CourseGpsMarker) => marker.locationType !== LOCATION_TYPE.Fairway)
  }, [markers])

  const holeModels = useMemo((): HoleGpsModel[] => {
    const holeMap = new Map<number, HoleGpsModel>()

    markers.forEach((marker: CourseGpsMarker) => {
      const defaultColor = marker.hole < 10 ? LINE_COLOR.Front9 : LINE_COLOR.Back9
      const existing = holeMap.get(marker.hole)

      const holeModel: HoleGpsModel = existing || {
        hole: marker.hole,
        color: marker.color || defaultColor,
      }

      if (marker.locationType === LOCATION_TYPE.Teebox) {
        holeModel.teebox = marker
      }

      if (marker.locationType === LOCATION_TYPE.Fairway) {
        holeModel.fairway = marker
      }

      if (marker.locationType === LOCATION_TYPE.Green) {
        holeModel.green = marker
      }

      if (!existing) {
        holeMap.set(marker.hole, holeModel)
      }
    })

    return Array.from(holeMap.values()).sort((a, b) => a.hole - b.hole)
  }, [markers])

  const holeModelsByHole = useMemo(() => {
    return new Map<number, HoleGpsModel>(holeModels.map((holeModel) => [holeModel.hole, holeModel]))
  }, [holeModels])

  const maxHoleWithPlayableMarkers = useMemo(() => {
    return holeModels.reduce((max, holeModel) => {
      if (!holeModel.teebox && !holeModel.green) {
        return max
      }

      return Math.max(max, holeModel.hole)
    }, 0)
  }, [holeModels])
  const playableHoles = useMemo(() => {
    return holeModels.filter((holeModel) => holeModel.teebox && holeModel.green).map((holeModel) => holeModel.hole)
  }, [holeModels])
  const playableHoleCount = useMemo(() => {
    return holeModels.reduce((count, holeModel) => {
      if (!holeModel.teebox || !holeModel.green) {
        return count
      }

      return count + 1
    }, 0)
  }, [holeModels])
  const activeHoleGreen = holeModelsByHole.get(activeHole)?.green
  const activeHoleGreenLat =
    typeof activeHoleGreen?.lat === 'string' ? parseFloat(activeHoleGreen.lat) : activeHoleGreen?.lat
  const activeHoleGreenLng =
    typeof activeHoleGreen?.lng === 'string' ? parseFloat(activeHoleGreen.lng) : activeHoleGreen?.lng

  const trimLinePath = useCallback(
    (path: CourseGpsMarker[]): CourseGpsMarker[] => {
      if (path.length < 2) {
        return path
      }

      const markerSizePx = getScaledGreenMarkerSizePx(zoom)
      const endpointOffsetPx = markerSizePx * LINE_ENDPOINT_OFFSET_RATIO
      const lastIndex = path.length - 1

      const parseMarkerLatLng = (marker: CourseGpsMarker) => {
        const lat = typeof marker.lat === 'string' ? parseFloat(marker.lat) : marker.lat
        const lng = typeof marker.lng === 'string' ? parseFloat(marker.lng) : marker.lng

        if (lat === null || lng === null || !Number.isFinite(lat) || !Number.isFinite(lng)) {
          return null
        }

        return { lat, lng }
      }

      const start = parseMarkerLatLng(path[0])
      const nextToStart = parseMarkerLatLng(path[1])
      const end = parseMarkerLatLng(path[lastIndex])
      const previousToEnd = parseMarkerLatLng(path[lastIndex - 1])

      if (!start || !nextToStart || !end || !previousToEnd) {
        return path
      }

      if (path.length === 2) {
        const [startX, startY] = latLonToGlobalPx(start.lat, start.lng, zoom)
        const [endX, endY] = latLonToGlobalPx(end.lat, end.lng, zoom)
        const dx = endX - startX
        const dy = endY - startY
        const distancePx = Math.hypot(dx, dy)

        if (distancePx <= endpointOffsetPx * 2 || distancePx === 0) {
          return path
        }

        const unitX = dx / distancePx
        const unitY = dy / distancePx
        const trimmedStartX = startX + unitX * endpointOffsetPx
        const trimmedStartY = startY + unitY * endpointOffsetPx
        const trimmedEndX = endX - unitX * endpointOffsetPx
        const trimmedEndY = endY - unitY * endpointOffsetPx

        const [trimmedStartLat, trimmedStartLng] = globalPxToLatLon(trimmedStartX, trimmedStartY, zoom)
        const [trimmedEndLat, trimmedEndLng] = globalPxToLatLon(trimmedEndX, trimmedEndY, zoom)

        return [
          {
            ...path[0],
            lat: trimmedStartLat,
            lng: trimmedStartLng,
          },
          {
            ...path[lastIndex],
            lat: trimmedEndLat,
            lng: trimmedEndLng,
          },
        ]
      }

      const updatedPath = path.slice()

      const [startX, startY] = latLonToGlobalPx(start.lat, start.lng, zoom)
      const [nextX, nextY] = latLonToGlobalPx(nextToStart.lat, nextToStart.lng, zoom)
      const startDx = nextX - startX
      const startDy = nextY - startY
      const startDistancePx = Math.hypot(startDx, startDy)

      if (startDistancePx > endpointOffsetPx && startDistancePx !== 0) {
        const startUnitX = startDx / startDistancePx
        const startUnitY = startDy / startDistancePx
        const trimmedStartX = startX + startUnitX * endpointOffsetPx
        const trimmedStartY = startY + startUnitY * endpointOffsetPx
        const [trimmedStartLat, trimmedStartLng] = globalPxToLatLon(trimmedStartX, trimmedStartY, zoom)

        updatedPath[0] = {
          ...updatedPath[0],
          lat: trimmedStartLat,
          lng: trimmedStartLng,
        }
      }

      const [endX, endY] = latLonToGlobalPx(end.lat, end.lng, zoom)
      const [previousX, previousY] = latLonToGlobalPx(previousToEnd.lat, previousToEnd.lng, zoom)
      const endDx = previousX - endX
      const endDy = previousY - endY
      const endDistancePx = Math.hypot(endDx, endDy)

      if (endDistancePx > endpointOffsetPx && endDistancePx !== 0) {
        const endUnitX = endDx / endDistancePx
        const endUnitY = endDy / endDistancePx
        const trimmedEndX = endX + endUnitX * endpointOffsetPx
        const trimmedEndY = endY + endUnitY * endpointOffsetPx
        const [trimmedEndLat, trimmedEndLng] = globalPxToLatLon(trimmedEndX, trimmedEndY, zoom)

        updatedPath[lastIndex] = {
          ...updatedPath[lastIndex],
          lat: trimmedEndLat,
          lng: trimmedEndLng,
        }
      }

      return updatedPath
    },
    [zoom],
  )

  const stackedHoleLayouts = useMemo((): StackedHoleLayout[] => {
    if (holeModels.length === 0) {
      return []
    }

    const groupedLayouts = new Map<string, StackedHoleLayout>()

    holeModels.forEach((holeModel) => {
      if (!holeModel.teebox || !holeModel.green) {
        return
      }

      const layoutKey = getHoleLayoutKey(holeModel)

      if (!layoutKey) {
        return
      }

      const basePath = holeModel.fairway
        ? [holeModel.teebox, holeModel.fairway, holeModel.green]
        : [holeModel.teebox, holeModel.green]
      const trimmedPath = trimLinePath(basePath)
      const path = toLatLngPath(trimmedPath)

      if (!path || path.length < 2) {
        return
      }

      const existingLayout = groupedLayouts.get(layoutKey)

      if (existingLayout) {
        existingLayout.holes.push(holeModel.hole)
        return
      }

      groupedLayouts.set(layoutKey, {
        key: layoutKey,
        holes: [holeModel.hole],
        representativeHole: holeModel.hole,
        teebox: holeModel.teebox,
        fairway: holeModel.fairway,
        green: holeModel.green,
        rawPath: basePath,
        path,
      })
    })

    return Array.from(groupedLayouts.values()).sort((a, b) => a.representativeHole - b.representativeHole)
  }, [holeModels, trimLinePath])

  const stackedHoleLayoutsByHole = useMemo(() => {
    const groupedByHole = new Map<number, StackedHoleLayout>()

    stackedHoleLayouts.forEach((layout) => {
      layout.holes.forEach((hole) => {
        groupedByHole.set(hole, layout)
      })
    })

    return groupedByHole
  }, [stackedHoleLayouts])

  const activeHoleStack = useMemo(() => {
    return stackedHoleLayoutsByHole.get(activeHole) || null
  }, [activeHole, stackedHoleLayoutsByHole])

  const activeHoleStackHoles = useMemo(() => {
    return activeHoleStack?.holes || [activeHole]
  }, [activeHole, activeHoleStack])

  const activeHoleRepresentativeHole = activeHoleStack?.representativeHole || activeHole

  const displayMarkers = useMemo((): DisplayMarker[] => {
    const visibleMarkerKeys = new Set<string>()

    return nonFairwayMarkers.reduce<DisplayMarker[]>((groupedMarkers, marker) => {
      const layout = stackedHoleLayoutsByHole.get(marker.hole)

      if (!layout) {
        groupedMarkers.push({
          ...marker,
          stackKey: `${marker.hole}-${marker.locationType}`,
          holes: [marker.hole],
          representativeHole: marker.hole,
          isActive: marker.hole === activeHole,
          color: marker.color || (marker.hole < 10 ? LINE_COLOR.Front9 : LINE_COLOR.Back9),
        })
        return groupedMarkers
      }

      const groupedMarkerKey = `${layout.key}-${marker.locationType}`

      if (visibleMarkerKeys.has(groupedMarkerKey)) {
        return groupedMarkers
      }

      visibleMarkerKeys.add(groupedMarkerKey)

      const representativeMarker = marker.locationType === LOCATION_TYPE.Green ? layout.green : layout.teebox

      groupedMarkers.push({
        ...representativeMarker,
        stackKey: groupedMarkerKey,
        holes: layout.holes,
        representativeHole: layout.representativeHole,
        isActive: layout.holes.includes(activeHole),
        color: representativeMarker.color || (layout.representativeHole < 10 ? LINE_COLOR.Front9 : LINE_COLOR.Back9),
      })

      return groupedMarkers
    }, [])
  }, [activeHole, nonFairwayMarkers, stackedHoleLayoutsByHole])

  const buildStackHoleLabel = useCallback((holes: number[]): string => {
    return holes
      .slice()
      .sort((a, b) => a - b)
      .join('/')
  }, [])

  const originalVersionMarkers: CourseGpsMarker[] = useMemo(() => {
    const gpsPoints = course?.gpsPoints || []
    return gpsPoints
      .slice()
      .filter((gps: CourseGps) => gps.gpsVersion === selectedGpsVersion)
      .sort((a, b) => {
        if (a.locationType && b.locationType && a.hole === b.hole) {
          return a.locationType.localeCompare(b.locationType)
        }
        return b.hole - a.hole
      })
      .map((gps: CourseGps): CourseGpsMarker => {
        let fairwayLat = gps.lat
        let fairwayLng = gps.lng

        const isFairwayWithoutLatLng =
          gps.locationType === LOCATION_TYPE.Fairway && (gps.lat === null || gps.lng === null)
        const geoCoordinates = gps.additionalInfo?.geoCoordinates?.[0]

        if (isFairwayWithoutLatLng && geoCoordinates?.geometry?.type === 'MultiPoint') {
          const multipointCoordinates = geoCoordinates.geometry.coordinates

          if (
            Array.isArray(multipointCoordinates) &&
            multipointCoordinates.length > 0 &&
            Array.isArray(multipointCoordinates[0]) &&
            typeof multipointCoordinates[0][0] === 'number' &&
            typeof multipointCoordinates[0][1] === 'number'
          ) {
            fairwayLng = multipointCoordinates[0][0]
            fairwayLat = multipointCoordinates[0][1]
          }
        }

        return {
          lat: fairwayLat,
          lng: fairwayLng,
          hole: gps.hole,
          locationType: gps.locationType,
        }
      })
  }, [course, selectedGpsVersion])

  const originalVersionPolygons: GpsEditorPolygon[] = useMemo(() => {
    const gpsPoints = course?.gpsPoints || []
    const versionGpsPoints = gpsPoints
      .slice()
      .filter((gps: CourseGps) => gps.gpsVersion === selectedGpsVersion)
      .sort((a, b) => a.hole - b.hole)

    const parsedPolygons: GpsEditorPolygon[] = []

    for (const gpsPoint of versionGpsPoints) {
      if (gpsPoint.locationType !== LOCATION_TYPE.Green) {
        continue
      }

      const geoCoordinates = gpsPoint.additionalInfo?.geoCoordinates || []

      for (const geoFeature of geoCoordinates) {
        if (geoFeature.geometry?.type !== 'Polygon') {
          continue
        }

        const polygonCoordinates = geoFeature.geometry.coordinates

        if (!Array.isArray(polygonCoordinates) || polygonCoordinates.length === 0) {
          continue
        }

        const outerRing = polygonCoordinates[0]

        if (!Array.isArray(outerRing)) {
          continue
        }

        const parsedPoints = outerRing
          .filter(
            (coordinate) =>
              Array.isArray(coordinate) &&
              coordinate.length >= 2 &&
              typeof coordinate[0] === 'number' &&
              typeof coordinate[1] === 'number' &&
              Number.isFinite(coordinate[0]) &&
              Number.isFinite(coordinate[1]),
          )
          .map((coordinate) => ({
            lat: coordinate[1] as number,
            lng: coordinate[0] as number,
          }))

        if (parsedPoints.length < 3) {
          continue
        }

        const firstPoint = parsedPoints[0]
        const lastPoint = parsedPoints[parsedPoints.length - 1]
        const isClosedRing = firstPoint.lat === lastPoint.lat && firstPoint.lng === lastPoint.lng
        const editorPoints = isClosedRing ? parsedPoints.slice(0, -1) : parsedPoints

        if (editorPoints.length < 3) {
          continue
        }

        parsedPolygons.push({
          id: `polygon-gps-${gpsPoint.id}-${selectedGpsVersion || 0}`,
          hole: gpsPoint.hole,
          isFinished: true,
          points: editorPoints,
        })
        break
      }
    }

    return parsedPolygons
  }, [course, selectedGpsVersion])

  const activeHolePolygon = useMemo(() => {
    return (
      poiPolygons.find((polygon) => (polygon.hole ?? activeHoleRepresentativeHole) === activeHoleRepresentativeHole) ||
      poiPolygons.find((polygon) => activeHoleStackHoles.includes(polygon.hole ?? activeHoleRepresentativeHole))
    )
  }, [activeHoleRepresentativeHole, activeHoleStackHoles, poiPolygons])

  const stackedPolygons = useMemo((): StackedPolygonOverlay[] => {
    const groupedPolygons = new Map<
      number,
      {
        key: string
        holes: number[]
        representativeHole: number
        polygons: GpsEditorPolygon[]
      }
    >()

    poiPolygons.forEach((polygon) => {
      const polygonHole = polygon.hole ?? activeHole
      const layout = stackedHoleLayoutsByHole.get(polygonHole)
      const representativeHole = layout?.representativeHole ?? polygonHole
      const existingGroup = groupedPolygons.get(representativeHole)

      if (existingGroup) {
        existingGroup.polygons.push(polygon)
        return
      }

      groupedPolygons.set(representativeHole, {
        key: layout ? `stacked-polygon-${layout.key}` : polygon.id,
        holes: layout?.holes || [polygonHole],
        representativeHole,
        polygons: [polygon],
      })
    })

    return Array.from(groupedPolygons.values())
      .map((group) => ({
        key: group.key,
        holes: group.holes,
        representativeHole: group.representativeHole,
        polygon:
          group.polygons.find((polygon) => (polygon.hole ?? group.representativeHole) === group.representativeHole) ||
          group.polygons[0],
      }))
      .sort((a, b) => a.representativeHole - b.representativeHole)
  }, [activeHole, poiPolygons, stackedHoleLayoutsByHole])

  const isActiveHolePolygonFinished = Boolean(activeHolePolygon?.isFinished)
  const activeGreenAutoMapPreview = useMemo(() => {
    if (!greenAutoMapPreview || greenAutoMapPreview.hole !== activeHole) {
      return null
    }

    return greenAutoMapPreview
  }, [activeHole, greenAutoMapPreview])

  const calculateMiddlePosition = useCallback(
    (markers: CourseGpsMarker[]) => {
      const toLatLng = (marker: CourseGpsMarker) => {
        const lat = typeof marker.lat === 'string' ? parseFloat(marker.lat) : marker.lat
        const lng = typeof marker.lng === 'string' ? parseFloat(marker.lng) : marker.lng

        if (lat === null || lng === null || !Number.isFinite(lat) || !Number.isFinite(lng)) {
          return null
        }

        return { lat, lng }
      }

      const validMarkers = markers.map(toLatLng).filter((marker): marker is { lat: number; lng: number } => !!marker)

      if (validMarkers.length >= 2) {
        return {
          lat: (validMarkers[0].lat + validMarkers[1].lat) / 2,
          lng: (validMarkers[0].lng + validMarkers[1].lng) / 2,
        }
      }

      if (validMarkers.length === 1) {
        return validMarkers[0]
      }

      const fallbackPoint = (course.gpsPoints || [])
        .map((point: CourseGps) => toLatLng(point as CourseGpsMarker))
        .find((point) => point !== null)

      return fallbackPoint || { lat: 0, lng: 0 }
    },
    [course.gpsPoints],
  )

  useEffect(() => {
    const versionChanged = lastSyncedVersionRef.current !== selectedGpsVersion
    const canSyncVersionState = gpsVersionStatus === 'idle' || gpsVersionStatus === 'succeeded'

    if (canSyncVersionState && versionChanged) {
      setMarkers(originalVersionMarkers)
      setPoiPolygons(originalVersionPolygons)
      setGreenAutoMapPreview(null)
      activeDrawingPolygonIdRef.current = null
      lastSyncedVersionRef.current = selectedGpsVersion
      history.reset()
    }
  }, [gpsVersionStatus, history, originalVersionMarkers, originalVersionPolygons, selectedGpsVersion])

  useEffect(() => {
    if (course?.gpsPoints) {
      const filtered = course.gpsPoints
        .filter((gps: CourseGps) => gps.gpsVersion === selectedGpsVersion)
        .filter((gps: CourseGps) => gps.hole === 1)
        .filter((gps: CourseGps) => gps.locationType !== LOCATION_TYPE.Fairway)

      if (filtered.length > 0) {
        const center = calculateMiddlePosition(filtered)
        setCenter(center)
      }
    }
  }, [course, selectedGpsVersion, calculateMiddlePosition])

  const nextHoleToolTarget = useMemo(() => {
    const latestHoleModel = holeModels.reduce<HoleGpsModel | undefined>((latest, holeModel) => {
      if (!holeModel.teebox && !holeModel.green) {
        return latest
      }

      if (!latest || holeModel.hole > latest.hole) {
        return holeModel
      }

      return latest
    }, undefined)

    if (!latestHoleModel) {
      return {
        hole: 1,
        locationType: LOCATION_TYPE.Teebox,
      }
    }

    if (latestHoleModel.teebox && !latestHoleModel.green) {
      return {
        hole: latestHoleModel.hole,
        locationType: LOCATION_TYPE.Green,
      }
    }

    return {
      hole: latestHoleModel.hole + 1,
      locationType: LOCATION_TYPE.Teebox,
    }
  }, [holeModels])

  const toLatLngLiteral = useCallback((marker?: CourseGpsMarker) => {
    if (!marker) {
      return null
    }

    const lat = typeof marker.lat === 'string' ? parseFloat(marker.lat) : marker.lat
    const lng = typeof marker.lng === 'string' ? parseFloat(marker.lng) : marker.lng

    if (lat === null || lng === null || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null
    }

    return { lat, lng }
  }, [])

  const buildPointDragPath = useCallback(
    (
      session: {
        locationType: LOCATION_TYPE.Teebox | LOCATION_TYPE.Green
        staticPoint: google.maps.LatLngLiteral
        fairwayPoint: google.maps.LatLngLiteral | null
      },
      draggedPoint: google.maps.LatLngLiteral,
    ) => {
      if (session.fairwayPoint) {
        return session.locationType === LOCATION_TYPE.Teebox
          ? [draggedPoint, session.fairwayPoint, session.staticPoint]
          : [session.staticPoint, session.fairwayPoint, draggedPoint]
      }

      return session.locationType === LOCATION_TYPE.Teebox
        ? [draggedPoint, session.staticPoint]
        : [session.staticPoint, draggedPoint]
    },
    [],
  )

  const onPointMarkerDragStart = useCallback(
    (marker: DisplayMarker) => {
      if (marker.locationType !== LOCATION_TYPE.Teebox && marker.locationType !== LOCATION_TYPE.Green) {
        return
      }

      if (!greenMappingEnabled) {
        setActiveHole(marker.representativeHole ?? marker.hole)
      }

      history.recordBeforeChange()

      if (!map || !window.google?.maps) {
        return
      }

      const holeModel = holeModelsByHole.get(marker.representativeHole)
      const teeboxPoint = toLatLngLiteral(holeModel?.teebox)
      const greenPoint = toLatLngLiteral(holeModel?.green)

      if (!teeboxPoint || !greenPoint) {
        return
      }

      const fairwayPoint = toLatLngLiteral(holeModel?.fairway)
      const staticPoint = marker.locationType === LOCATION_TYPE.Teebox ? greenPoint : teeboxPoint
      const draggedPoint = marker.locationType === LOCATION_TYPE.Teebox ? teeboxPoint : greenPoint
      const strokeColor = marker.color || (marker.hole < 10 ? LINE_COLOR.Front9 : LINE_COLOR.Back9)

      pointMarkerDragSessionRef.current?.distanceLabelMarkers?.forEach((distanceMarker) => {
        distanceMarker.marker.map = null
      })
      pointMarkerDragSessionRef.current?.polyline?.setMap(null)

      const polyline = new window.google.maps.Polyline({
        map,
        ...getDashedLineOptions(strokeColor, getHoleZIndex(marker)),
      })

      const distanceLabelMarkers = fairwayPoint
        ? [createDistanceLabelMarker(map), createDistanceLabelMarker(map)].filter(
            (distanceMarker): distanceMarker is DistanceLabelMarker => Boolean(distanceMarker),
          )
        : [createDistanceLabelMarker(map)].filter((distanceMarker): distanceMarker is DistanceLabelMarker =>
            Boolean(distanceMarker),
          )

      pointMarkerDragSessionRef.current = {
        representativeHole: marker.representativeHole,
        holes: marker.holes,
        locationType: marker.locationType,
        staticPoint,
        fairwayPoint,
        polyline,
        distanceLabelMarkers,
        didDrag: false,
      }

      const previewPath = buildPointDragPath(
        {
          locationType: marker.locationType,
          staticPoint,
          fairwayPoint,
        },
        draggedPoint,
      )

      polyline.setPath(previewPath)

      if (previewPath.length === 3 && distanceLabelMarkers.length >= 2) {
        setDistanceLabel(distanceLabelMarkers[0], previewPath[0], previewPath[1])
        setDistanceLabel(distanceLabelMarkers[1], previewPath[1], previewPath[2])
      } else if (distanceLabelMarkers[0] && previewPath.length >= 2) {
        setPathDistanceLabel(distanceLabelMarkers[0], previewPath)
      }

      setDraggingMainMarkerHole(marker.representativeHole)
    },
    [buildPointDragPath, getDashedLineOptions, history, holeModelsByHole, map, markers, toLatLngLiteral],
  )

  const onPointMarkerDrag = useCallback(
    (event: google.maps.MapMouseEvent) => {
      const session = pointMarkerDragSessionRef.current
      const latLng = event.latLng

      if (!session || !latLng) {
        return
      }

      session.didDrag = true

      const draggedPoint = { lat: latLng.lat(), lng: latLng.lng() }
      const previewPath = buildPointDragPath(session, draggedPoint)
      session.polyline?.setPath(previewPath)

      if (previewPath.length === 3 && session.distanceLabelMarkers.length >= 2) {
        setDistanceLabel(session.distanceLabelMarkers[0], previewPath[0], previewPath[1])
        setDistanceLabel(session.distanceLabelMarkers[1], previewPath[1], previewPath[2])
      } else if (session.distanceLabelMarkers[0] && previewPath.length >= 2) {
        setPathDistanceLabel(session.distanceLabelMarkers[0], previewPath)
      }

      if (!session.fairwayPoint) {
        const holeNumberMarker = holeNumberMarkerRefs.current.get(session.representativeHole)

        if (holeNumberMarker) {
          holeNumberMarker.position = {
            lat: (session.staticPoint.lat + draggedPoint.lat) / 2,
            lng: (session.staticPoint.lng + draggedPoint.lng) / 2,
          }
        }
      }
    },
    [buildPointDragPath],
  )

  const onPointMarkerDragEnd = useCallback(
    (marker: DisplayMarker, event: google.maps.MapMouseEvent) => {
      const session = pointMarkerDragSessionRef.current

      pointMarkerDragSessionRef.current?.distanceLabelMarkers?.forEach((distanceMarker) => {
        distanceMarker.marker.map = null
      })
      pointMarkerDragSessionRef.current?.polyline?.setMap(null)
      pointMarkerDragSessionRef.current = null
      setDraggingMainMarkerHole(null)

      const lat = event.latLng?.lat()
      const lng = event.latLng?.lng()

      if (lat === undefined || lng === undefined) {
        return
      }

      if (!session) {
        return
      }

      const updatedMarkers = markers.map((existingMarker) => {
        if (!session.holes.includes(existingMarker.hole) || existingMarker.locationType !== marker.locationType) {
          return existingMarker
        }

        return {
          ...existingMarker,
          lat,
          lng,
          isDragging: false,
        }
      })

      setMarkers(updatedMarkers)

      if (
        marker.locationType === LOCATION_TYPE.Green &&
        greenAutoMapControlsVisible &&
        session.holes.includes(activeHole)
      ) {
        runGreenToolAutoMapRef.current?.(
          activeHole,
          { green: { lat, lng }, teebox: session.staticPoint },
          greenAutoMapParamsRef.current,
        )
      }
    },
    [activeHole, greenAutoMapControlsVisible, markers],
  )

  const autoLoad = useCallback((e) => {
    setMapSearch(e)
  }, [])

  const placeChanged = useCallback(() => {
    if (!mapSearch) {
      return
    }

    const place = mapSearch.getPlace()
    if (!place?.geometry?.location) {
      return
    }

    const { lat, lng } = place.geometry.location
    setCenter({
      lat: lat(),
      lng: lng(),
    })
    setZoom(DEFAULT_COURSE_ZOOM)
  }, [mapSearch])

  const syncSelectedVersion = useCallback(
    (version: number) => {
      if (version === selectedGpsVersion) {
        return
      }

      pendingGreenAutoAdvanceRef.current = null
      setGuidedGreenAutoMapSession(null)
      setGuidedGreenAutoMapCompletionVisible(false)

      const creatingNewVersion = course?.id > 0 && version === 0
      const versionChanged = course?.id > 0 && version && selectedGpsVersion !== version
      if (creatingNewVersion || versionChanged) {
        setSelectedGpsVersion(version)
      }

      if ((creatingNewVersion && markers.length === 0) || versionChanged) {
        setActiveHole(1)
      }

      const creatingAdditionalVersion = creatingNewVersion && course?.gpsPoints && course.gpsPoints.length > 0
      if (creatingAdditionalVersion || versionChanged) {
        setZoom(DEFAULT_HOLE_ZOOM)
      }
    },
    [course, selectedGpsVersion, markers],
  )

  const syncHoleTool = useCallback((currentState: boolean) => {
    setHoleToolEnabled(currentState)
  }, [])

  const syncGreenMapping = useCallback(
    (currentState: boolean) => {
      setGreenMappingEnabled(currentState)

      if (!currentState) {
        pendingGreenAutoAdvanceRef.current = null
        setGuidedGreenAutoMapSession(null)
        setGuidedGreenAutoMapCompletionVisible(false)
        setGreenAutoMapControlsVisible(false)
        setGreenAutoMapPreview(null)
        activeDrawingPolygonIdRef.current = null

        if (activeHolePolygon && !activeHolePolygon.isFinished) {
          setPoiPolygons((prev) =>
            prev.filter((polygon) => !activeHoleStackHoles.includes(polygon.hole ?? activeHoleRepresentativeHole)),
          )
        }
      }
    },
    [activeHolePolygon, activeHoleRepresentativeHole, activeHoleStackHoles],
  )

  useEffect(() => {
    if (!greenMappingEnabled) {
      return
    }

    const lat = activeHoleGreenLat
    const lng = activeHoleGreenLng

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return
    }

    const greenLatLng = { lat: Number(lat), lng: Number(lng) }
    setCenter(greenLatLng)

    let cancelled = false
    getMaxImageryZoom(greenLatLng).then((maxImageryZoom) => {
      if (cancelled) {
        return
      }
      // Google's MaxZoomService returns the deepest level with native tiles, but Google Maps client
      // scales those tiles up one or two more levels before going blank. +1 is safe
      const clampedZoom = maxImageryZoom === null ? MAX_MAP_ZOOM : Math.min(MAX_MAP_ZOOM, maxImageryZoom + 1)
      setZoom(clampedZoom)
    })
    return () => {
      cancelled = true
    }
  }, [activeHoleGreenLat, activeHoleGreenLng, getMaxImageryZoom, greenMappingEnabled])

  const hasFinishedGreenMappingForHole = useCallback(
    (hole: number, polygons: GpsEditorPolygon[]) => {
      const stackedLayout = stackedHoleLayoutsByHole.get(hole)
      const relevantHoles = stackedLayout?.holes || [hole]

      return polygons.some((polygon) => Boolean(polygon.isFinished) && relevantHoles.includes(polygon.hole ?? hole))
    },
    [stackedHoleLayoutsByHole],
  )

  const findNextGuidedGreenAutoMapHole = useCallback(
    (holes: number[], polygons: GpsEditorPolygon[], currentHole?: number): number | null => {
      if (holes.length === 0) {
        return null
      }

      const currentIndex = currentHole === undefined ? -1 : holes.indexOf(currentHole)

      for (let offset = 1; offset <= holes.length; offset += 1) {
        const nextIndex = (currentIndex + offset + holes.length) % holes.length
        const hole = holes[nextIndex]

        if (!hasFinishedGreenMappingForHole(hole, polygons)) {
          return hole
        }
      }

      return null
    },
    [hasFinishedGreenMappingForHole],
  )

  const guidedGreenAutoMapVerifiedCount = useMemo(() => {
    if (!guidedGreenAutoMapSession) {
      return 0
    }

    return guidedGreenAutoMapSession.holes.reduce((count, hole) => {
      return count + (hasFinishedGreenMappingForHole(hole, poiPolygons) ? 1 : 0)
    }, 0)
  }, [guidedGreenAutoMapSession, hasFinishedGreenMappingForHole, poiPolygons])

  const getHoleGreenAutoMapInput = useCallback(
    (hole: number): { green: { lat: number; lng: number }; teebox: { lat: number; lng: number } } | null => {
      const holeModel = holeModelsByHole.get(hole)
      const holeGreen = holeModel?.green
      const holeTeebox = holeModel?.teebox

      if (!holeGreen || !holeTeebox) {
        return null
      }

      const greenLat = typeof holeGreen.lat === 'string' ? parseFloat(holeGreen.lat) : holeGreen.lat
      const greenLng = typeof holeGreen.lng === 'string' ? parseFloat(holeGreen.lng) : holeGreen.lng
      const teeboxLat = typeof holeTeebox.lat === 'string' ? parseFloat(holeTeebox.lat) : holeTeebox.lat
      const teeboxLng = typeof holeTeebox.lng === 'string' ? parseFloat(holeTeebox.lng) : holeTeebox.lng

      if (![greenLat, greenLng, teeboxLat, teeboxLng].every((value) => Number.isFinite(value))) {
        return null
      }

      return {
        green: { lat: Number(greenLat), lng: Number(greenLng) },
        teebox: { lat: Number(teeboxLat), lng: Number(teeboxLng) },
      }
    },
    [holeModelsByHole],
  )

  const updateGreenAutoMapParam = useCallback(
    (key: keyof GreenAutoMapParams, value: GreenAutoMapParams[keyof GreenAutoMapParams]) => {
      setGreenAutoMapParams((prev) => {
        if (prev[key] === value) {
          return prev
        }

        return {
          ...prev,
          [key]: value,
        }
      })
    },
    [],
  )

  const buildMainMarkerContent = useCallback(
    (marker: CourseGpsMarker) => {
      if (!window.DOMParser) {
        return document.createElement('div')
      }

      const locationType = marker.locationType === LOCATION_TYPE.Green ? LOCATION_TYPE.Green : LOCATION_TYPE.Teebox
      const isGreen = locationType === LOCATION_TYPE.Green
      const { width, height } = getMainMarkerDimensions(locationType, zoom)
      const iconMarkup = renderToStaticMarkup(
        isGreen ? (
          <GreenMarkerIcon fill="#FFFFFF" width={Math.round(width)} height={Math.round(height)} />
        ) : (
          <TeeboxMarkerIcon fill="#FFFFFF" width={Math.round(width)} height={Math.round(height)} />
        ),
      )
      const parser = new DOMParser()
      const svgElement = parser.parseFromString(iconMarkup, 'image/svg+xml').documentElement as unknown as SVGElement
      svgElement.classList.add('gps-editor-main-marker-svg')

      return svgElement as unknown as HTMLElement
    },
    [zoom],
  )

  const getMainMarkerAnchor = useCallback((marker: CourseGpsMarker) => {
    if (marker.locationType === LOCATION_TYPE.Green) {
      return {
        anchorLeft: '-50%',
        anchorTop: '-50%',
      }
    }

    return {
      anchorLeft: '-50%',
      anchorTop: '-100%',
    }
  }, [])

  const buildHoleNumberMarkerContent = useCallback(
    (holes: number[], isActive: boolean) => {
      const height = Math.min(42, Math.max(24, Math.round(zoom * 2.5)))
      const fontSize = Math.min(16, Math.max(10, Math.round(zoom * 0.8)))
      const container = document.createElement('div')

      container.className = isActive
        ? 'gps-editor-hole-number-marker gps-editor-hole-number-marker-active'
        : 'gps-editor-hole-number-marker'
      container.style.setProperty('--gps-hole-marker-height', `${height}px`)
      container.style.setProperty('--gps-hole-marker-padding-x', `${Math.round(height * 0.25)}px`)

      const text = document.createElement('span')
      text.textContent = buildStackHoleLabel(holes)
      text.className = 'gps-editor-hole-number-marker-text'
      text.style.setProperty('--gps-hole-marker-font-size', `${fontSize}px`)

      container.appendChild(text)

      return container
    },
    [buildStackHoleLabel, zoom],
  )

  const buildPolygonStartPointContent = useCallback(() => {
    const hitArea = document.createElement('div')
    hitArea.style.width = '28px'
    hitArea.style.height = '28px'
    hitArea.style.borderRadius = '50%'
    hitArea.style.background = 'transparent'
    return hitArea
  }, [])

  const centerMapToActiveHole = useCallback(
    (newHole: number) => {
      const activeHole = holeModelsByHole.get(newHole)

      const markersForCenter: CourseGpsMarker[] = activeHole
        ? [activeHole.teebox, activeHole.green, activeHole.fairway].filter((marker): marker is CourseGpsMarker =>
            Boolean(marker),
          )
        : []

      const center = calculateMiddlePosition(markersForCenter)
      setCenter(center)
      setZoom(DEFAULT_HOLE_ZOOM)
    },
    [calculateMiddlePosition, holeModelsByHole],
  )

  const goToPreviousHole = useCallback(() => {
    const maxHole = maxHoleWithPlayableMarkers
    if (maxHole === 0) {
      return
    }

    let newHole = activeHole - 1
    if (activeHole === 1) {
      newHole = maxHole
    }
    setActiveHole(newHole)
    centerMapToActiveHole(newHole)
  }, [activeHole, centerMapToActiveHole, maxHoleWithPlayableMarkers])

  const goToNextHole = useCallback(() => {
    const maxHole = maxHoleWithPlayableMarkers
    if (maxHole === 0) {
      return
    }

    let newHole = activeHole + 1
    if (activeHole === maxHole || newHole > maxHole) {
      newHole = 1
    }
    setActiveHole(newHole)
    centerMapToActiveHole(newHole)
  }, [activeHole, centerMapToActiveHole, maxHoleWithPlayableMarkers])

  const goToHole = useCallback(
    (hole: number) => {
      setActiveHole(hole)

      if (holeModelsByHole.has(hole)) {
        centerMapToActiveHole(hole)
      }
    },
    [centerMapToActiveHole, holeModelsByHole],
  )

  const resetMarkers = useCallback(
    (newMarkers?: CourseGpsMarker[], newPolygons?: GpsEditorPolygon[]) => {
      const updatedMarkers = newMarkers || originalVersionMarkers
      const updatedPolygons = newPolygons || []

      setMarkers(updatedMarkers)
      setPoiPolygons(updatedPolygons)
      setGreenAutoMapPreview(null)
      pointMarkerDragSessionRef.current?.distanceLabelMarkers?.forEach((distanceMarker) => {
        distanceMarker.marker.map = null
      })
      pointMarkerDragSessionRef.current?.polyline?.setMap(null)
      pointMarkerDragSessionRef.current = null
      fairwayDragSessionRef.current?.distanceLabelMarkers?.forEach((labelMarker) => {
        labelMarker.marker.map = null
      })
      fairwayDragSessionRef.current?.polyline?.setMap(null)
      fairwayDragSessionRef.current = null
      setDraggingMainMarkerHole(null)
      setDraggingFairwayHole(null)
      pendingGreenAutoAdvanceRef.current = null
      setGuidedGreenAutoMapSession(null)
      setGuidedGreenAutoMapCompletionVisible(false)
      activeDrawingPolygonIdRef.current = null
      setActiveHole(1)
    },
    [originalVersionMarkers],
  )

  const getCursor = () => {
    if (greenMappingEnabled && !isActiveHolePolygonFinished && !activeGreenAutoMapPreview) {
      return 'crosshair'
    }

    if (nonFairwayMarkers.length === MAX_GPS_POINTS || !holeToolEnabled) {
      return 'default'
    }
    const cursor = nextHoleToolTarget.locationType === LOCATION_TYPE.Teebox ? CursorTeebox : CursorGreen
    return 'url(' + cursor + '), auto'
  }

  const handleHoleToolClick = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (!holeToolEnabled || nonFairwayMarkers.length === MAX_GPS_POINTS || !event.latLng) {
        return
      }

      const { lat, lng } = event.latLng
      const nextLocationType = nextHoleToolTarget.locationType
      const nextHole = nextHoleToolTarget.hole

      if (nextHole > 18) {
        return
      }

      const newMarker: CourseGpsMarker = {
        lat: lat(),
        lng: lng(),
        hole: nextHole,
        locationType: nextLocationType,
      }

      history.recordBeforeChange()
      setMarkers([...markers, newMarker])
      setActiveHole(nextHole)
    },
    [history, holeToolEnabled, markers, nextHoleToolTarget, nonFairwayMarkers.length],
  )

  function getDashedLineOptions(strokeColor: string, zIndex?: number): google.maps.PolylineOptions {
    return {
      strokeColor,
      strokeOpacity: 0,
      strokeWeight: LINE_STROKE_WEIGHT,
      zIndex,
      icons: [
        {
          icon: {
            // Dashed line pattern: 6x4 px dash with 4px gaps
            path: 'M -2 -2 L 2 -2 L 2 4 L -2 4 z',
            fillColor: strokeColor,
            fillOpacity: 1,
            strokeWeight: 0,
            scale: 1,
          },
          offset: '2px',
          repeat: '10px',
        },
      ],
    }
  }

  function getLineOptions(linePath: CourseGpsMarker[]): google.maps.PolylineOptions {
    const isDragging = linePath.some((m: CourseGpsMarker) => m.isDragging === true)
    const strokeColor = linePath[0]?.color || LINE_COLOR.Front9

    if (isDragging) {
      return getDashedLineOptions(strokeColor)
    }

    return {
      strokeColor,
      strokeOpacity: 1,
      strokeWeight: LINE_STROKE_WEIGHT,
      icons: [],
    }
  }

  const triggerZoom = useCallback(
    (direction: '+' | '-') => {
      if (direction === '+') {
        map?.setZoom((map.getZoom() || 0) + 1)
        return
      }
      map?.setZoom((map.getZoom() || 0) - 1)
    },
    [map],
  )

  const resetZoom = useCallback(() => {
    map?.setZoom(DEFAULT_ZOOM_WITHOUT_COURSE)
  }, [map])

  const isUserAdmin = () => {
    if (isAdmin || isAdminCourseEditor) {
      return true
    }
    return false
  }

  const triggerMapTilt = useCallback(() => {
    if (!map) {
      return
    }
    map.setTilt(map.getTilt() === 0 ? 45 : 0)
  }, [map])

  useEffect(() => {
    if (!map) {
      setMapTiltEnabled(false)
      return
    }
    const sync = () => setMapTiltEnabled((map.getTilt() ?? 0) > 0)
    sync()
    const listener = map.addListener('tilt_changed', sync)
    return () => listener.remove()
  }, [map])

  useEffect(() => {
    if (!map) {
      setMapTiltAvailable(false)
      return
    }

    const mapDiv = map.getDiv() as HTMLElement | null
    if (!mapDiv) {
      setMapTiltAvailable(false)
      return
    }

    let bundledControlObserver: MutationObserver | null = null
    let lookupObserver: MutationObserver | null = null

    const watchBundledControl = (control: HTMLElement) => {
      let pendingShowTimer: number | null = null
      const clearPendingShow = () => {
        if (pendingShowTimer !== null) {
          window.clearTimeout(pendingShowTimer)
          pendingShowTimer = null
        }
      }
      const apply = () => {
        const visible = control.style.display !== 'none'
        if (visible) {
          if (pendingShowTimer !== null) {
            return
          }
          pendingShowTimer = window.setTimeout(() => {
            pendingShowTimer = null
            setMapTiltAvailable(true)
          }, 400)
          return
        }
        clearPendingShow()
        setMapTiltAvailable((prev) => {
          if (prev) {
            map.setTilt(0)
            setMapTiltEnabled(false)
          }
          return false
        })
      }
      apply()
      bundledControlObserver = new MutationObserver(apply)
      bundledControlObserver.observe(control, { attributes: true, attributeFilter: ['style', 'class'] })
      const previousDisconnect = bundledControlObserver.disconnect.bind(bundledControlObserver)
      bundledControlObserver.disconnect = () => {
        clearPendingShow()
        previousDisconnect()
      }
    }

    const tryFindControl = (): boolean => {
      const tiltButton = mapDiv.querySelector<HTMLButtonElement>('button.gm-tilt')
      const container = tiltButton?.closest('.gm-bundled-control')
      if (!container) {
        return false
      }
      watchBundledControl(container as HTMLElement)
      return true
    }

    if (!tryFindControl()) {
      lookupObserver = new MutationObserver(() => {
        if (tryFindControl()) {
          lookupObserver?.disconnect()
          lookupObserver = null
        }
      })
      lookupObserver.observe(mapDiv, { childList: true, subtree: true })
    }

    return () => {
      bundledControlObserver?.disconnect()
      lookupObserver?.disconnect()
    }
  }, [map])

  const triggerMapRotate = useCallback(
    (direction: 'clockwise' | 'counterclockwise') => {
      if (!map) {
        return
      }
      const step = 90
      const currentHeading = map.getHeading() ?? 0
      const delta = direction === 'clockwise' ? -step : step
      const nextHeading = (((currentHeading + delta) % 360) + 360) % 360
      map.setHeading(nextHeading)
    },
    [map],
  )

  const handleSubmit = useCallback(
    async (makeOfficial = false, populateRemainingHoles = false) => {
      if (course.id > 0 && markers.length > 0) {
        const shouldPopulateRemainingHoles = populateRemainingHoles && playableHoleCount > 0 && playableHoleCount < 18
        const completeCourseGpsData = shouldPopulateRemainingHoles
          ? buildCompleteCourseGpsData(markers, poiPolygons)
          : { markers, polygons: poiPolygons }
        const markersToSave = completeCourseGpsData.markers
        const polygonsToSave = completeCourseGpsData.polygons
        const roundCoordinate = (value: number) => Number(value.toFixed(6))

        const greenPolygonCoordinatesByHole = new Map<number, number[][][]>()

        polygonsToSave.forEach((polygon) => {
          if (!polygon.isFinished || polygon.hole === undefined) {
            return
          }

          const coordinates = polygon.points
            .map((point) => [roundCoordinate(point.lng), roundCoordinate(point.lat)] as [number, number])
            .filter(([lng, lat]) => Number.isFinite(lng) && Number.isFinite(lat))

          if (coordinates.length < 3) {
            return
          }

          const firstPoint = coordinates[0]
          const lastPoint = coordinates[coordinates.length - 1]
          const isRingClosed = firstPoint[0] === lastPoint[0] && firstPoint[1] === lastPoint[1]

          if (!isRingClosed) {
            coordinates.push(firstPoint)
          }

          greenPolygonCoordinatesByHole.set(polygon.hole, [coordinates])
        })

        const gpsPoints: CourseGpsVersionData[] = markersToSave.map((gps: CourseGpsMarker) => {
          const lat = typeof gps.lat === 'string' ? parseFloat(gps.lat) : gps.lat
          const lng = typeof gps.lng === 'string' ? parseFloat(gps.lng) : gps.lng
          const roundedLat = lat !== null && Number.isFinite(lat) ? roundCoordinate(lat) : null
          const roundedLng = lng !== null && Number.isFinite(lng) ? roundCoordinate(lng) : null

          const isFairwayPoint = gps.locationType === LOCATION_TYPE.Fairway
          const isGreenPoint = gps.locationType === LOCATION_TYPE.Green
          const hasValidFairwayCoordinates = roundedLat !== null && roundedLng !== null
          const greenPolygonCoordinates = greenPolygonCoordinatesByHole.get(gps.hole)

          let additionalInfo: CourseGpsVersionData['additionalInfo']

          if (isFairwayPoint && hasValidFairwayCoordinates) {
            additionalInfo = {
              geoCoordinates: [
                {
                  type: 'Feature',
                  geometry: {
                    type: 'MultiPoint',
                    coordinates: [[roundedLng, roundedLat]],
                  },
                },
              ],
            }
          }

          if (isGreenPoint && greenPolygonCoordinates) {
            additionalInfo = {
              geoCoordinates: [
                {
                  type: 'Feature',
                  geometry: {
                    type: 'Polygon',
                    coordinates: greenPolygonCoordinates,
                  },
                },
              ],
            }
          }

          return {
            hole: gps.hole,
            accuracy: null,
            lat: isFairwayPoint ? null : roundedLat,
            lng: isFairwayPoint ? null : roundedLng,
            locationType: gps.locationType,
            source: isUserAdmin() ? COURSE_DATA_SOURCE.GGB_ADMIN : COURSE_DATA_SOURCE.GGB_USER,
            additionalInfo,
          }
        })
        const payload: CourseGpsVersionSavePayload = {
          courseId: course.id,
          isUserEditor: isGpsEditor,
          data: { gpsPoints },
        }

        const result = await dispatch(createCourseGpsVersion(payload))
        const canMakeOfficial =
          makeOfficial && (playableHoleCount === 18 || playableHoleCount === 1 || shouldPopulateRemainingHoles)

        if (!canMakeOfficial || !createCourseGpsVersion.fulfilled.match(result)) {
          return
        }

        const savedCourse = result.payload
        const latestGpsVersion = (savedCourse.gpsPoints || []).reduce((latest, gpsPoint: CourseGps) => {
          return Math.max(latest, gpsPoint.gpsVersion || 0)
        }, 0)

        if (latestGpsVersion > 0) {
          dispatch(
            setCourseGpsVersionOfficial({
              courseId: savedCourse.id,
              data: { gpsVersion: latestGpsVersion },
            }),
          )
        }
      }
    },
    [course, dispatch, isGpsEditor, markers, poiPolygons],
  )

  const isVersionDataChanged = useCallback(() => {
    const normalizeMarkers = (versionMarkers: CourseGpsMarker[]) => {
      return versionMarkers
        .slice()
        .sort((a, b) => {
          if (a.hole !== b.hole) {
            return a.hole - b.hole
          }

          return (a.locationType || '').localeCompare(b.locationType || '')
        })
        .map((marker) => ({
          lat: marker.lat,
          lng: marker.lng,
          hole: marker.hole,
          locationType: marker.locationType,
        }))
    }

    const normalizePolygons = (versionPolygons: GpsEditorPolygon[]) => {
      return versionPolygons
        .slice()
        .sort((a, b) => {
          const holeDelta = (a.hole ?? 0) - (b.hole ?? 0)

          if (holeDelta !== 0) {
            return holeDelta
          }

          return a.points.length - b.points.length
        })
        .map((polygon) => ({
          hole: polygon.hole ?? null,
          isFinished: Boolean(polygon.isFinished),
          points: polygon.points.map((point) => ({
            lat: point.lat,
            lng: point.lng,
          })),
        }))
    }

    return compareData(
      {
        markers: normalizeMarkers(markers),
        polygons: normalizePolygons(poiPolygons),
      },
      {
        markers: normalizeMarkers(originalVersionMarkers),
        polygons: normalizePolygons(originalVersionPolygons),
      },
    )
  }, [markers, originalVersionMarkers, originalVersionPolygons, poiPolygons])

  const getHoleZIndex = (marker: CourseGpsMarker) => {
    if (activeHole > 9) {
      return marker.hole > 9 ? 1 : 0
    }
    return marker.hole > 9 ? 0 : 1
  }

  const getHoleMarkerPosition = useCallback(
    (representativeHole: number, linePath: CourseGpsMarker[]): { lat: number; lng: number } => {
      const fairwayMarker = holeModelsByHole.get(representativeHole)?.fairway

      if (fairwayMarker) {
        const lat = typeof fairwayMarker.lat === 'string' ? parseFloat(fairwayMarker.lat) : fairwayMarker.lat
        const lng = typeof fairwayMarker.lng === 'string' ? parseFloat(fairwayMarker.lng) : fairwayMarker.lng

        if (lat !== null && lng !== null && Number.isFinite(lat) && Number.isFinite(lng)) {
          return { lat, lng }
        }
      }

      return calculateMiddlePosition(linePath)
    },
    [calculateMiddlePosition, holeModelsByHole],
  )

  const onHoleMarkerDragStart = useCallback(
    (line: StackedHoleLayout) => {
      if (!greenMappingEnabled) {
        setActiveHole(line.representativeHole)
      }

      history.recordBeforeChange()

      const linePath = line.rawPath

      if (linePath.length < 2) {
        return
      }

      const teeboxLat = linePath[0].lat
      const teeboxLng = linePath[0].lng
      const greenLat = linePath[linePath.length - 1].lat
      const greenLng = linePath[linePath.length - 1].lng

      if (
        teeboxLat === null ||
        teeboxLng === null ||
        greenLat === null ||
        greenLng === null ||
        ![teeboxLat, teeboxLng, greenLat, greenLng].every((value) => Number.isFinite(value))
      ) {
        return
      }

      fairwayDragSessionRef.current?.distanceLabelMarkers?.forEach((distanceMarker) => {
        distanceMarker.marker.map = null
      })
      fairwayDragSessionRef.current?.polyline?.setMap(null)

      fairwayDragSessionRef.current = {
        representativeHole: line.representativeHole,
        holes: line.holes,
        teebox: { lat: teeboxLat, lng: teeboxLng },
        green: { lat: greenLat, lng: greenLng },
        strokeColor: linePath[0]?.color || LINE_COLOR.Front9,
        zIndex: getHoleZIndex(linePath[0]),
        didDrag: false,
        polyline: null,
        distanceLabelMarkers: [],
      }
    },
    [getHoleZIndex, history, markers],
  )

  const onHoleMarkerDragEnd = useCallback(
    (representativeHole: number, event: google.maps.MapMouseEvent) => {
      const session = fairwayDragSessionRef.current
      const latLng = event.latLng
      const didDrag = session?.didDrag === true

      const lat = latLng?.lat()
      const lng = latLng?.lng()

      if (lat === undefined || lng === undefined) {
        session?.distanceLabelMarkers?.forEach((labelMarker) => {
          labelMarker.marker.map = null
        })
        session?.polyline?.setMap(null)
        fairwayDragSessionRef.current = null
        setDraggingFairwayHole(null)
        return
      }

      session?.distanceLabelMarkers?.forEach((labelMarker) => {
        labelMarker.marker.map = null
      })
      session?.polyline?.setMap(null)
      fairwayDragSessionRef.current = null
      setDraggingFairwayHole(null)

      if (!didDrag) {
        return
      }

      const updatedMarkers = markers
        .filter(
          (marker: CourseGpsMarker) =>
            !(session.holes.includes(marker.hole) && marker.locationType === LOCATION_TYPE.Fairway),
        )
        .concat(
          session.holes.map((hole) => ({
            hole,
            lat,
            lng,
            locationType: LOCATION_TYPE.Fairway,
            isDragging: false,
          })),
        )

      setMarkers(updatedMarkers)
    },
    [markers],
  )

  const onHoleMarkerDrag = useCallback(
    (representativeHole: number, event: google.maps.MapMouseEvent) => {
      const session = fairwayDragSessionRef.current

      if (!session || session.representativeHole !== representativeHole) {
        return
      }

      const latLng = event.latLng

      if (!latLng) {
        return
      }

      if (!session.polyline) {
        if (!map || !window.google?.maps) {
          return
        }

        session.polyline = new window.google.maps.Polyline({
          map,
          ...getDashedLineOptions(session.strokeColor, session.zIndex),
        })

        const firstSegmentLabelMarker = createDistanceLabelMarker(map)
        const secondSegmentLabelMarker = createDistanceLabelMarker(map)
        session.distanceLabelMarkers = [firstSegmentLabelMarker, secondSegmentLabelMarker].filter(
          (distanceMarker): distanceMarker is DistanceLabelMarker => Boolean(distanceMarker),
        )
      }

      if (!session.didDrag) {
        setDraggingFairwayHole(session.representativeHole)
      }
      session.didDrag = true

      const fairwayPoint = { lat: latLng.lat(), lng: latLng.lng() }

      session.polyline?.setPath([session.teebox, fairwayPoint, session.green])

      if (session.distanceLabelMarkers[0]) {
        setDistanceLabel(session.distanceLabelMarkers[0], session.teebox, fairwayPoint)
      }

      if (session.distanceLabelMarkers[1]) {
        setDistanceLabel(session.distanceLabelMarkers[1], fairwayPoint, session.green)
      }
    },
    [getDashedLineOptions, map],
  )

  const resetFairwayForLine = useCallback(
    (line: StackedHoleLayout) => {
      const holesSet = new Set(line.holes)
      const hasFairwayMarker = markers.some(
        (marker) => holesSet.has(marker.hole) && marker.locationType === LOCATION_TYPE.Fairway,
      )
      if (!hasFairwayMarker) {
        return
      }

      history.recordBeforeChange()
      setMarkers((prev) =>
        prev.filter((marker) => !(holesSet.has(marker.hole) && marker.locationType === LOCATION_TYPE.Fairway)),
      )
    },
    [history, markers],
  )

  const runGreenToolAutoMap = useCallback(
    async (
      hole: number,
      holeInput: { green: { lat: number; lng: number }; teebox: { lat: number; lng: number } },
      params: GreenAutoMapParams,
    ) => {
      if (!map || isProcessingPOI.current) {
        return
      }

      const stackHoles = stackedHoleLayoutsByHole.get(hole)?.holes || [hole]
      const hasExistingPolygon = poiPolygons.some((polygon) => stackHoles.includes(polygon.hole ?? -1))
      if (hasExistingPolygon) {
        history.recordBeforeChange()
        setPoiPolygons((prev) => prev.filter((polygon) => !stackHoles.includes(polygon.hole ?? -1)))
        activeDrawingPolygonIdRef.current = null
      }

      isProcessingPOI.current = true
      setGreenAutoMapInFlight(true)
      const requestId = ++poiRequestIdRef.current

      try {
        const result = await runGreenAutoMapForHole({
          green: holeInput.green,
          teebox: holeInput.teebox,
          params,
          googleMapsApiKey: apiKey,
        })

        if (!isMountedRef.current || requestId !== poiRequestIdRef.current) {
          return
        }

        const polygonPoints: { lat: number; lng: number }[] = []
        for (let i = 0; i < result.rayCount; i++) {
          const dirKey = String(i + 1)
          const edgePoint = result.edgePoints[dirKey]

          if (!edgePoint) {
            continue
          }

          polygonPoints.push({
            lat: edgePoint.lat,
            lng: edgePoint.lng,
          })
        }

        if (polygonPoints.length === 0 || !isMountedRef.current || requestId !== poiRequestIdRef.current) {
          return
        }

        setGreenAutoMapPreview({
          hole,
          center: holeInput.green,
          edgePoints: polygonPoints,
        })
        activeDrawingPolygonIdRef.current = null
      } finally {
        if (requestId === poiRequestIdRef.current) {
          isProcessingPOI.current = false
          setGreenAutoMapInFlight(false)
        }
      }
    },
    [apiKey, history, map, poiPolygons, stackedHoleLayoutsByHole],
  )

  useEffect(() => {
    runGreenToolAutoMapRef.current = runGreenToolAutoMap
  }, [runGreenToolAutoMap])

  const lastAutoMapResetStateRef = useRef<{ activeHole: number; greenMappingEnabled: boolean }>({
    activeHole,
    greenMappingEnabled,
  })

  useEffect(() => {
    const previousState = lastAutoMapResetStateRef.current
    const activeHoleChanged = previousState.activeHole !== activeHole
    const greenMappingTurnedOff = previousState.greenMappingEnabled && !greenMappingEnabled
    lastAutoMapResetStateRef.current = { activeHole, greenMappingEnabled }

    const pendingAutoAdvance = pendingGreenAutoAdvanceRef.current

    if (
      pendingAutoAdvance &&
      pendingAutoAdvance.hole === activeHole &&
      greenMappingEnabled &&
      pendingAutoAdvance.shouldAutoMap &&
      pendingAutoAdvance.holeInput
    ) {
      pendingGreenAutoAdvanceRef.current = null
      setGreenAutoMapControlsVisible(true)
      setGreenAutoMapPreview(null)
      runGreenToolAutoMap(activeHole, pendingAutoAdvance.holeInput, {
        ...greenAutoMapParamsRef.current,
        rayCount: DEFAULT_GREEN_AUTO_MAP_PARAMS.rayCount,
      })
      return
    }

    if (!activeHoleChanged && !greenMappingTurnedOff) {
      return
    }

    pendingGreenAutoAdvanceRef.current = null
    poiRequestIdRef.current += 1
    setGreenAutoMapControlsVisible(false)
    setGreenAutoMapPreview(null)
  }, [activeHole, greenMappingEnabled, runGreenToolAutoMap])

  const handleGreenToolAutoMap = useCallback(
    (paramsOverride?: GreenAutoMapParams) => {
      if (!greenMappingEnabled) {
        return
      }

      const holeInput = getHoleGreenAutoMapInput(activeHole)

      if (!holeInput) {
        return
      }

      const nextParams = paramsOverride || greenAutoMapParams

      setGreenAutoMapControlsVisible(true)
      runGreenToolAutoMap(activeHole, holeInput, nextParams)
    },
    [activeHole, getHoleGreenAutoMapInput, greenAutoMapParams, greenMappingEnabled, runGreenToolAutoMap],
  )

  const startGuidedGreenAutoMap = useCallback(() => {
    if (playableHoles.length !== 18) {
      return
    }

    const sessionHoles = playableHoles.slice()
    const nextHole = findNextGuidedGreenAutoMapHole(sessionHoles, poiPolygons)

    if (!nextHole) {
      pendingGreenAutoAdvanceRef.current = null
      setGuidedGreenAutoMapSession(null)
      setGuidedGreenAutoMapCompletionVisible(true)
      setGreenAutoMapControlsVisible(false)
      setGreenAutoMapPreview(null)
      return
    }

    const nextHoleInput = getHoleGreenAutoMapInput(nextHole)

    setGuidedGreenAutoMapSession({ holes: sessionHoles })
    setGuidedGreenAutoMapCompletionVisible(false)
    setGreenAutoMapPreview(null)
    activeDrawingPolygonIdRef.current = null

    pendingGreenAutoAdvanceRef.current = {
      hole: nextHole,
      holeInput: nextHoleInput,
      shouldAutoMap: Boolean(nextHoleInput),
    }

    if (nextHole === activeHole && nextHoleInput) {
      pendingGreenAutoAdvanceRef.current = null
      setGreenAutoMapControlsVisible(true)
      runGreenToolAutoMap(nextHole, nextHoleInput, {
        ...greenAutoMapParamsRef.current,
        rayCount: DEFAULT_GREEN_AUTO_MAP_PARAMS.rayCount,
      })
      return
    }

    setActiveHole(nextHole)
    centerMapToActiveHole(nextHole)
  }, [
    activeHole,
    centerMapToActiveHole,
    findNextGuidedGreenAutoMapHole,
    getHoleGreenAutoMapInput,
    playableHoles,
    poiPolygons,
    runGreenToolAutoMap,
  ])

  const resetGreenAutoMapParams = useCallback(() => {
    const remappableKeys = (Object.keys(DEFAULT_GREEN_AUTO_MAP_PARAMS) as (keyof GreenAutoMapParams)[]).filter(
      (key) => !GREEN_AUTO_MAP_PERSISTED_DEBUG_KEYS.includes(key as GreenAutoMapPersistedDebugKey),
    )
    const nextParams: GreenAutoMapParams = {
      ...DEFAULT_GREEN_AUTO_MAP_PARAMS,
      debugStopLogging: greenAutoMapParams.debugStopLogging,
      debugImageLogging: greenAutoMapParams.debugImageLogging,
    }
    const hasResetChanges = remappableKeys.some((key) => greenAutoMapParams[key] !== nextParams[key])

    if (!hasResetChanges) {
      return
    }

    setGreenAutoMapParams(nextParams)
    handleGreenToolAutoMap(nextParams)
  }, [greenAutoMapParams, handleGreenToolAutoMap])

  const closeGreenAutoMapControls = useCallback(() => {
    setGreenAutoMapControlsVisible(false)
    setGreenAutoMapPreview(null)
  }, [])

  const closeGuidedGreenAutoMapCompletion = useCallback(() => {
    setGuidedGreenAutoMapCompletionVisible(false)
  }, [])

  const syncPolygonsForHoles = useCallback(
    (
      previousPolygons: GpsEditorPolygon[],
      holes: number[],
      points: GpsEditorPolygonPoint[],
      isFinished: boolean,
    ): GpsEditorPolygon[] => {
      const otherPolygons = previousPolygons.filter((polygon) => !holes.includes(polygon.hole ?? -1))
      const polygonsByHole = new Map(
        previousPolygons
          .filter((polygon) => holes.includes(polygon.hole ?? -1))
          .map((polygon) => [polygon.hole as number, polygon]),
      )

      const syncedPolygons = holes.map((hole) => {
        const existingPolygon = polygonsByHole.get(hole)

        return {
          id: existingPolygon?.id || `polygon-${hole}-${crypto.randomUUID()}`,
          hole,
          isFinished,
          points: clonePolygonPoints(points),
        }
      })

      return [...otherPolygons, ...syncedPolygons]
    },
    [],
  )

  const acceptGreenAutoMapPreview = useCallback(() => {
    if (!activeGreenAutoMapPreview || activeGreenAutoMapPreview.edgePoints.length < 3) {
      return
    }

    const nextPolygons = syncPolygonsForHoles(
      poiPolygons,
      activeHoleStackHoles,
      activeGreenAutoMapPreview.edgePoints,
      true,
    )
    const nextGuidedHole = guidedGreenAutoMapSession
      ? findNextGuidedGreenAutoMapHole(guidedGreenAutoMapSession.holes, nextPolygons, activeHole)
      : null

    history.recordBeforeChange()
    setPoiPolygons(nextPolygons)
    setGreenAutoMapPreview(null)
    activeDrawingPolygonIdRef.current = null

    if (!guidedGreenAutoMapSession) {
      pendingGreenAutoAdvanceRef.current = null
      return
    }

    if (!nextGuidedHole) {
      pendingGreenAutoAdvanceRef.current = null
      setGuidedGreenAutoMapSession(null)
      setGuidedGreenAutoMapCompletionVisible(true)
      setGreenAutoMapControlsVisible(false)
      return
    }

    const nextHoleInput = getHoleGreenAutoMapInput(nextGuidedHole)

    pendingGreenAutoAdvanceRef.current = {
      hole: nextGuidedHole,
      holeInput: nextHoleInput,
      shouldAutoMap: Boolean(nextHoleInput),
    }

    setActiveHole(nextGuidedHole)
    centerMapToActiveHole(nextGuidedHole)
  }, [
    activeGreenAutoMapPreview,
    activeHole,
    activeHoleStackHoles,
    centerMapToActiveHole,
    findNextGuidedGreenAutoMapHole,
    getHoleGreenAutoMapInput,
    guidedGreenAutoMapSession,
    history,
    poiPolygons,
    syncPolygonsForHoles,
  ])

  const confirmManualGuidedGreen = useCallback(() => {
    if (!guidedGreenAutoMapSession) {
      return
    }
    if (!activeHolePolygon?.isFinished) {
      return
    }

    const nextGuidedHole = findNextGuidedGreenAutoMapHole(guidedGreenAutoMapSession.holes, poiPolygons, activeHole)

    if (!nextGuidedHole) {
      pendingGreenAutoAdvanceRef.current = null
      setGuidedGreenAutoMapSession(null)
      setGuidedGreenAutoMapCompletionVisible(true)
      setGreenAutoMapControlsVisible(false)
      return
    }

    const nextHoleInput = getHoleGreenAutoMapInput(nextGuidedHole)

    pendingGreenAutoAdvanceRef.current = {
      hole: nextGuidedHole,
      holeInput: nextHoleInput,
      shouldAutoMap: Boolean(nextHoleInput),
    }

    setActiveHole(nextGuidedHole)
    centerMapToActiveHole(nextGuidedHole)
  }, [
    activeHole,
    activeHolePolygon,
    centerMapToActiveHole,
    findNextGuidedGreenAutoMapHole,
    getHoleGreenAutoMapInput,
    guidedGreenAutoMapSession,
    poiPolygons,
  ])

  const updateGreenAutoMapPreviewPoint = useCallback(
    (pointIndex: number, nextPoint: google.maps.LatLngLiteral) => {
      setGreenAutoMapPreview((prev) => {
        if (!prev || prev.hole !== activeHole || !prev.edgePoints[pointIndex]) {
          return prev
        }

        const nextEdgePoints = prev.edgePoints.slice()
        nextEdgePoints[pointIndex] = nextPoint

        return {
          ...prev,
          edgePoints: nextEdgePoints,
        }
      })
    },
    [activeHole],
  )

  const handleFinishGreenPolygon = useCallback(() => {
    const polygon = activeHolePolygon
    if (!polygon || polygon.points.length < 3) {
      return
    }

    history.recordBeforeChange()
    setPoiPolygons((prev) => syncPolygonsForHoles(prev, activeHoleStackHoles, polygon.points, true))
    activeDrawingPolygonIdRef.current = null
  }, [activeHolePolygon, activeHoleStackHoles, history, syncPolygonsForHoles])

  const handleClearGreenPolygon = useCallback(() => {
    if (!activeHolePolygon) {
      return
    }

    history.recordBeforeChange()

    setPoiPolygons((prev) => prev.filter((polygon) => !activeHoleStackHoles.includes(polygon.hole ?? -1)))
    activeDrawingPolygonIdRef.current = null
  }, [activeHolePolygon, activeHoleStackHoles, history])

  const handleGreenToolClick = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (!greenMappingEnabled || !event.latLng) {
        return
      }

      if (activeGreenAutoMapPreview) {
        return
      }

      const vertexIndex = (event as any).vertex as number | undefined
      const currentPolygon =
        poiPolygons.find(
          (polygon) => (polygon.hole ?? activeHoleRepresentativeHole) === activeHoleRepresentativeHole,
        ) || poiPolygons.find((polygon) => activeHoleStackHoles.includes(polygon.hole ?? activeHoleRepresentativeHole))

      if (vertexIndex === 0 && currentPolygon && !currentPolygon.isFinished && currentPolygon.points.length >= 3) {
        handleFinishGreenPolygon()
        return
      }

      // Ignore clicks on polygons or markers
      if ((event as any).placeId || vertexIndex !== undefined) {
        return
      }

      const point = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      }

      if (currentPolygon?.isFinished) {
        return
      }

      history.recordBeforeChange()

      const nextPoints = currentPolygon ? [...currentPolygon.points, point] : [point]
      setPoiPolygons((prev) => syncPolygonsForHoles(prev, activeHoleStackHoles, nextPoints, false))
    },
    [
      activeGreenAutoMapPreview,
      activeHole,
      activeHoleRepresentativeHole,
      activeHoleStackHoles,
      greenMappingEnabled,
      handleFinishGreenPolygon,
      history,
      poiPolygons,
      syncPolygonsForHoles,
    ],
  )

  const handleMapClick = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (greenMappingEnabled) {
        handleGreenToolClick(event)
        return
      }

      handleHoleToolClick(event)
    },
    [greenMappingEnabled, handleHoleToolClick, handleGreenToolClick],
  )

  const updatePolygonPoints = useCallback(
    (holes: number[], polygon: GpsEditorPolygon, updatedPath: google.maps.LatLngLiteral[]) => {
      const parsedPoints = updatedPath
        .filter((point) => Number.isFinite(point.lat) && Number.isFinite(point.lng))
        .map((point) => ({
          lat: point.lat,
          lng: point.lng,
        }))

      if (parsedPoints.length === 0) {
        return
      }

      history.recordBeforeChange()
      setPoiPolygons((prev) => syncPolygonsForHoles(prev, holes, parsedPoints, Boolean(polygon.isFinished)))
    },
    [history, syncPolygonsForHoles],
  )

  const mapControlProps = useMemo(() => {
    return {
      activeHole,
      markers,
      polygons: poiPolygons,
      maxGpsPoints: MAX_GPS_POINTS,
      versionIsDirty: isVersionDataChanged(),
      autoLoad,
      placeChanged,
      syncSelectedVersion,
      selectedGpsVersion,
      resetMarkers,
      resetZoom,
      syncHoleTool,
      syncGreenMapping,
      greenAutoMapControlsVisible,
      greenAutoMapBusy: greenAutoMapInFlight,
      greenAutoMapParams,
      onGreenAutoMapParamChange: updateGreenAutoMapParam,
      onResetGreenAutoMapParams: resetGreenAutoMapParams,
      greenAutoMapPreviewReady: Boolean(activeGreenAutoMapPreview?.edgePoints.length),
      onCloseGreenAutoMapControls: closeGreenAutoMapControls,
      onAcceptGreenAutoMapPreview: acceptGreenAutoMapPreview,
      onConfirmManualGuidedGreen: confirmManualGuidedGreen,
      guidedGreenAutoMapActive: Boolean(guidedGreenAutoMapSession),
      guidedGreenAutoMapCompletedOpen: guidedGreenAutoMapCompletionVisible,
      guidedGreenAutoMapVerifiedCount,
      guidedGreenAutoMapTotalCount: guidedGreenAutoMapSession?.holes.length || 0,
      onCloseGuidedGreenAutoMapCompleted: closeGuidedGreenAutoMapCompletion,
      onStartGuidedGreenAutoMap: startGuidedGreenAutoMap,
      greenPolygonFinished: isActiveHolePolygonFinished,
      onAutoMapGreen: handleGreenToolAutoMap,
      onFinishGreenPolygon: handleFinishGreenPolygon,
      onClearGreenPolygon: handleClearGreenPolygon,
      goToPreviousHole,
      goToNextHole,
      goToHole,
      triggerZoom,
      triggerMapTilt,
      triggerMapRotate,
      mapTiltEnabled,
      mapTiltAvailable,
      handleSubmit,
      playableHoleCount,
      canUndo: history.canUndo,
      canRedo: history.canRedo,
      onUndo: history.undo,
      onRedo: history.redo,
      onRecordBeforeChange: history.recordBeforeChange,
      registerActivateGreenTool,
    }
  }, [
    activeHole,
    markers,
    poiPolygons,
    isVersionDataChanged,
    autoLoad,
    placeChanged,
    syncSelectedVersion,
    selectedGpsVersion,
    resetMarkers,
    resetZoom,
    syncHoleTool,
    syncGreenMapping,
    greenAutoMapControlsVisible,
    greenAutoMapInFlight,
    greenAutoMapParams,
    updateGreenAutoMapParam,
    activeGreenAutoMapPreview,
    closeGreenAutoMapControls,
    acceptGreenAutoMapPreview,
    confirmManualGuidedGreen,
    guidedGreenAutoMapSession,
    guidedGreenAutoMapCompletionVisible,
    guidedGreenAutoMapVerifiedCount,
    closeGuidedGreenAutoMapCompletion,
    startGuidedGreenAutoMap,
    isActiveHolePolygonFinished,
    handleGreenToolAutoMap,
    handleFinishGreenPolygon,
    handleClearGreenPolygon,
    goToPreviousHole,
    goToNextHole,
    goToHole,
    triggerZoom,
    triggerMapTilt,
    triggerMapRotate,
    mapTiltEnabled,
    mapTiltAvailable,
    handleSubmit,
    playableHoleCount,
    history,
    registerActivateGreenTool,
  ])

  if (!isLoaded || !auth.siteConfig?.googleMapsApiKey) {
    return (
      <SpinnerContainer>
        <LoadingSpinnerGreen size={72} />
      </SpinnerContainer>
    )
  }

  return (
    <Container>
      <MarkerGlobalStyles />
      <GoogleMap
        mapContainerStyle={{
          margin: '0',
          width: '100%',
          height: '100vh',
        }}
        mapContainerClassName="map-root"
        center={center}
        zoom={zoom}
        onLoad={onLoad}
        onClick={handleMapClick}
        onZoomChanged={handleZoomChanged}
        options={{
          mapId: DEFAULT_MAP_ID,
          mapTypeId: window.google.maps.MapTypeId.HYBRID,
          cameraControl: false,
          mapTypeControl: false,
          streetViewControl: false,
          zoomControl: false,
          fullscreenControl: false,
          rotateControl: true,
          draggableCursor: getCursor(),
          minZoom: 3,
          restriction: {
            latLngBounds: {
              north: 85,
              south: -85,
              west: -180,
              east: 180,
            },
          },
        }}
      >
        {displayMarkers.map((m: any) => {
          const markerAnchor = getMainMarkerAnchor(m)

          return (
            <MapAdvancedMarker
              key={`${m.hole}-${m.locationType}`}
              map={map}
              position={m}
              draggable
              anchorLeft={markerAnchor.anchorLeft}
              anchorTop={markerAnchor.anchorTop}
              buildContent={() => buildMainMarkerContent(m)}
              onHostClick={() => setActiveHole(m.representativeHole ?? m.hole)}
              onDragStart={() => onPointMarkerDragStart(m)}
              onDrag={(event) => onPointMarkerDrag(event)}
              onDragEnd={(event) => onPointMarkerDragEnd(m, event)}
              zIndex={getHoleZIndex(m)}
            />
          )
        })}
        {stackedHoleLayouts.map((line) => {
          if (greenMappingEnabled && line.holes.includes(activeHole)) {
            return null
          }

          const isDraggingLineForHole =
            draggingFairwayHole === line.representativeHole || draggingMainMarkerHole === line.representativeHole

          const lineOptions = {
            ...getLineOptions(line.rawPath),
            zIndex: getHoleZIndex(line.rawPath[0]),
          }

          return (
            <Fragment key={`line-${line.key}`}>
              {!isDraggingLineForHole && <MapPolylineOverlay map={map} path={line.path} options={lineOptions} />}
              <MapAdvancedMarker
                map={map}
                position={getHoleMarkerPosition(line.representativeHole, line.rawPath)}
                draggable
                anchorLeft="-50%"
                anchorTop="-50%"
                buildContent={() => buildHoleNumberMarkerContent(line.holes, line.holes.includes(activeHole))}
                onLoad={(holeMarker) => {
                  holeNumberMarkerRefs.current.set(line.representativeHole, holeMarker)
                }}
                onUnmount={() => {
                  holeNumberMarkerRefs.current.delete(line.representativeHole)
                }}
                onHostClick={() => setActiveHole(line.representativeHole)}
                onDragStart={() => onHoleMarkerDragStart(line)}
                onDrag={(event) => onHoleMarkerDrag(line.representativeHole, event)}
                onDragEnd={(event) => onHoleMarkerDragEnd(line.representativeHole, event)}
                onDoubleClick={() => resetFairwayForLine(line)}
                zIndex={getHoleZIndex(line.rawPath[0])}
              />
            </Fragment>
          )
        })}
        {stackedPolygons.map(({ key, holes, polygon }) => {
          const isPolygonFinished = Boolean(polygon.isFinished)
          const isEditablePolygon = greenMappingEnabled && holes.includes(activeHole)

          if (!isPolygonFinished) {
            return (
              <Fragment key={key}>
                <MapPolylineOverlay
                  map={map}
                  path={polygon.points}
                  onClick={(event) => {
                    if (!isEditablePolygon) {
                      return
                    }
                    if (event.vertex === 0 && polygon.points.length >= 3) {
                      handleFinishGreenPolygon()
                    }
                  }}
                  onPathChanged={(updatedPath) => {
                    if (!isEditablePolygon) {
                      return
                    }

                    updatePolygonPoints(holes, polygon, updatedPath)
                  }}
                  options={{
                    strokeColor: '#00AA00',
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                    clickable: isEditablePolygon,
                    editable: isEditablePolygon,
                    zIndex: GREEN_POLYGON_Z_INDEX,
                  }}
                />
                {isEditablePolygon && polygon.points.length >= 3 && (
                  <MapAdvancedMarker
                    key={`${key}-start-point`}
                    map={map}
                    position={polygon.points[0]}
                    buildContent={buildPolygonStartPointContent}
                    onClick={(event) => {
                      event?.domEvent?.stopPropagation?.()
                      handleFinishGreenPolygon()
                    }}
                    zIndex={101}
                  />
                )}
              </Fragment>
            )
          }

          return (
            <MapPolygonOverlay
              key={key}
              map={map}
              paths={polygon.points}
              onPathChanged={(updatedPath) => {
                if (!isEditablePolygon) {
                  return
                }

                updatePolygonPoints(holes, polygon, updatedPath)
              }}
              onClick={(e) => {
                e.stop()
                const targetHole = holes[0] ?? polygon.hole
                if (typeof targetHole === 'number') {
                  setActiveHole(targetHole)
                }
              }}
              onDoubleClick={(e) => {
                e.stop()
                const targetHole = holes[0] ?? polygon.hole
                if (typeof targetHole === 'number') {
                  setActiveHole(targetHole)
                }
                activateGreenToolRef.current?.()
              }}
              options={{
                fillColor: '#00FF00',
                fillOpacity: isPolygonFinished ? 0.3 : 0,
                strokeColor: '#00AA00',
                strokeOpacity: 0.8,
                strokeWeight: 2,
                clickable: true,
                editable: isEditablePolygon,
                zIndex: GREEN_POLYGON_Z_INDEX,
              }}
            />
          )
        })}
        {activeGreenAutoMapPreview && (
          <Fragment>
            {activeGreenAutoMapPreview.edgePoints.map((point, index) => {
              const previewLineStart = getOffsetPreviewLineStart(activeGreenAutoMapPreview.center, point, zoom)

              return (
                <Fragment key={`green-auto-map-preview-${index}`}>
                  <MapPolylineOverlay
                    map={map}
                    path={[previewLineStart, point]}
                    options={{
                      clickable: false,
                      editable: false,
                      geodesic: false,
                      icons: [
                        {
                          icon: {
                            path: 'M 0,-1 0,1',
                            scale: 2,
                            strokeOpacity: 1,
                            strokeColor: '#ffffff',
                          },
                          offset: '0',
                          repeat: '12px',
                        },
                      ],
                      strokeOpacity: 0,
                      strokeWeight: 2,
                      zIndex: GREEN_AUTO_MAP_PREVIEW_Z_INDEX,
                    }}
                  />
                  <MapAdvancedMarker
                    map={map}
                    position={point}
                    draggable
                    anchorLeft="-50%"
                    anchorTop="-50%"
                    buildContent={() => {
                      const pointElement = document.createElement('div')

                      pointElement.style.width = '12px'
                      pointElement.style.height = '12px'
                      pointElement.style.borderRadius = '50%'
                      pointElement.style.background = '#ffffff'

                      return pointElement
                    }}
                    onDragStart={() => {
                      history.recordBeforeChange()
                    }}
                    onDrag={(event) => {
                      const latLng = event?.latLng

                      if (!latLng) {
                        return
                      }

                      updateGreenAutoMapPreviewPoint(index, {
                        lat: latLng.lat(),
                        lng: latLng.lng(),
                      })
                    }}
                    onDragEnd={(event) => {
                      const latLng = event?.latLng

                      if (!latLng) {
                        return
                      }

                      updateGreenAutoMapPreviewPoint(index, {
                        lat: latLng.lat(),
                        lng: latLng.lng(),
                      })
                    }}
                    zIndex={GREEN_AUTO_MAP_PREVIEW_Z_INDEX + 1}
                  />
                </Fragment>
              )
            })}
            <MapPolygonOverlay
              key={'automap-preview-polygon'}
              map={map}
              paths={activeGreenAutoMapPreview.edgePoints}
              options={{
                fillColor: '#00FF00',
                fillOpacity: 0.3,
                strokeColor: '#00AA00',
                strokeOpacity: 0.8,
                strokeWeight: 2,
                clickable: false,
                editable: false,
                zIndex: GREEN_POLYGON_Z_INDEX,
              }}
            />
          </Fragment>
        )}
      </GoogleMap>
      <OverlayLayer>
        <CourseGpsEditorElements {...mapControlProps} />
      </OverlayLayer>
    </Container>
  )
}

const MarkerGlobalStyles = createGlobalStyle`
  .map-root .gm-bundled-control {
    opacity: 0 !important;
    pointer-events: none !important;
  }

  .gps-editor-advanced-marker-host,
  .gps-editor-advanced-marker-host:focus,
  .gps-editor-advanced-marker-host:focus-visible,
  .gps-editor-advanced-marker-host:active {
    outline: none !important;
    box-shadow: none !important;
  }

  .gps-editor-advanced-marker-host-draggable {
    cursor: grab !important;
  }

  .gps-editor-advanced-marker-host-draggable:active {
    cursor: grabbing !important;
  }

  .gps-editor-advanced-marker-host-draggable * {
    cursor: inherit !important;
  }

  img[src*='drag-cross'] {
    display: none !important;
    opacity: 0 !important;
    visibility: hidden !important;
  }

  .gps-editor-main-marker-svg {
    display: block;
    user-select: none;
  }

  .gps-editor-distance-label {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: 'Söhne';
    min-width: 44px;
    height: 24px;
    padding: 0 8px;
    border-radius: 999px;
    box-sizing: border-box;
    pointer-events: none;
    border-top: 0.5px solid rgba(48, 39, 39, 0.15);
    border-bottom: 0.5px solid rgba(255, 255, 255, 0.15);
    background: rgba(20, 24, 26, 0.3);
    backdrop-filter: blur(5px);
  }

  .gps-editor-distance-label-text {
    color: #fff;
    font-size: 14px;
    font-weight: 500;
    line-height: 1;
    white-space: nowrap;
  }

  .gps-editor-hole-number-marker {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-family: 'Söhne';
    min-width: var(--gps-hole-marker-height);
    height: var(--gps-hole-marker-height);
    padding: 0 var(--gps-hole-marker-padding-x);
    background: #ffffff;
    border: 0;
    border-radius: 999px;
    box-sizing: border-box;
    box-shadow: 0 0 0 4px rgba(226, 232, 240, 0.2);
  }

  .gps-editor-hole-number-marker-active {
    animation: gps-editor-hole-number-marker-pulse 1s infinite;
    animation-timing-function: cubic-bezier(0, 0, 0.2, 1);
  }

  @keyframes gps-editor-hole-number-marker-pulse {
    0% {
      box-shadow: 0 0 0 0px rgba(226, 232, 240, 0.8);
    }
    100% {
      box-shadow: 0 0 0 8px rgba(226, 232, 240, 0);
    }
  }

  .gps-editor-hole-number-marker-text {
    color: #000000;
    font-size: var(--gps-hole-marker-font-size);
    font-weight: 600;
    line-height: 1;
  }
`

const Container = styled.div`
  position: relative;

  & .gm-style-cc {
    display: none;
  }
`

const OverlayLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
`

const SpinnerContainer = styled.div`
  display: flex;
  width: 100%;
  height: 50vh;
  padding-top: 30vh;
  justify-content: center;
`

export default CourseGpsEditor
